import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { authAPI } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [waContext, setWaContext] = useState(null); // {cta_destination, campaign_id, wa_token}

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("spp_access_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
    } catch {
      localStorage.removeItem("spp_access_token");
      localStorage.removeItem("spp_refresh_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle wa_token from URL on mount.
  // setState calls inside runWaFlow are guarded by `cancelled` for cleanup —
  // this is a legitimate async-effect pattern.
  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(window.location.search);
    const upgrade = params.get("upgrade");

    // Defensive: WASimple/WhatsApp template URL misconfigurations sometimes
    // place the JWT into `fbclid` (or another param) instead of `wa_token`,
    // or include `wa_token` as a bare key with no value. Scan all params
    // for a JWT-shaped value (3 dot-separated base64url segments).
    const isJwtShaped = (v) =>
      typeof v === "string" &&
      v.length > 40 &&
      /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(v);

    let waToken = params.get("wa_token");
    let waTokenParamKey = "wa_token";
    if (!isJwtShaped(waToken)) {
      for (const [key, val] of params.entries()) {
        if (isJwtShaped(val)) {
          waToken = val;
          waTokenParamKey = key;
          break;
        }
      }
    }

    const runWaFlow = async () => {
      try {
        const res = await authAPI.validateWaToken(waToken);
        if (cancelled) return;
        if (res.data.valid && res.data.access_token) {
          localStorage.setItem("spp_access_token", res.data.access_token);
          localStorage.setItem("spp_refresh_token", res.data.refresh_token);
          setUser(res.data.user);

          const ctx = {
            cta_destination: res.data.cta_destination,
            campaign_id: res.data.campaign_id,
            wa_token: waToken,
          };
          setWaContext(ctx);
          sessionStorage.setItem("spp_wa_context", JSON.stringify(ctx));

          // Clean URL params (strip wherever the token was found + tracking)
          const url = new URL(window.location);
          url.searchParams.delete("wa_token");
          url.searchParams.delete("upgrade");
          url.searchParams.delete("fbclid");
          if (waTokenParamKey !== "wa_token" && waTokenParamKey !== "fbclid") {
            url.searchParams.delete(waTokenParamKey);
          }
          window.history.replaceState({}, "", url.pathname + url.search);
        } else {
          await checkAuth();
        }
      } catch {
        if (!cancelled) await checkAuth();
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (waToken && isJwtShaped(waToken)) {
      runWaFlow();
    } else {
      if (upgrade === "true") {
        sessionStorage.setItem("spp_show_upgrade", "true");
      }
      checkAuth();
    }

    return () => { cancelled = true; };
  }, [checkAuth]);

  const login = (accessToken, refreshToken, userData) => {
    localStorage.setItem("spp_access_token", accessToken);
    localStorage.setItem("spp_refresh_token", refreshToken);
    setUser(userData);
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem("spp_refresh_token");
    try {
      if (refreshToken) await authAPI.logout(refreshToken);
    } catch {
      // ignore logout errors
    }
    localStorage.removeItem("spp_access_token");
    localStorage.removeItem("spp_refresh_token");
    sessionStorage.removeItem("spp_wa_context");
    sessionStorage.removeItem("spp_show_upgrade");
    setUser(null);
    setWaContext(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth, waContext }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
